import React, { useEffect, useMemo, useRef, useState } from "react";
import SEO from "../components/SEO";
import "./Courses.css";
import brainImg from "../assets/brain.jpg";
import module1 from "../assets/kids2.jpeg";
import module2 from "../assets/kids3.jpeg";
import module3 from "../assets/kids1.jpeg";

function Courses() {
  const [brainActive, setBrainActive] = useState(false);
  const [activeModule, setActiveModule] = useState("junior");
  const contentRef = useRef(null);
  const tabsRef = useRef(null);

  const moduleData = useMemo(
    () => ({
      junior: {
        tab: "Junior Module",
        badge: "SIP Abacus Malkajgiri Junior",
        title: "Build confidence with numbers from an early age",
        description:
          "A skill development programme for children aged 6 to 8 years, designed to remove fear of numbers, improve focus, and strengthen brain coordination through structured Abacus learning.",
        stats: [
          { label: "Age Group", value: "6–8 years" },
          { label: "Class Level", value: "Class 1 & 2" },
          { label: "Session", value: "1 per week" },
          { label: "Duration", value: "12–16 months" },
        ],
        journey: {
          title: "From fear of numbers to confident calculation",
          text: "The SIP Abacus Malkajgiri Junior programme gently guides children through a transformation making numbers easier, learning enjoyable, and mental skills stronger.",
          before:
            "Many children feel unsure around numbers. Low confidence, poor concentration, and fear of calculations are common at this stage.",
          learning: [
            "Basic understanding of Abacus",
            "Removes fear of numbers",
            "Improves attention span",
            "Builds brain coordination",
            "Strengthens motor skills",
            "Learns tables from 2 to 9",
          ],
          after:
            "Children become quicker, more confident, and mentally stronger. They are prepared to move comfortably into the Foundation level.",
        },
        brain: true,
        detailsTitle: "Structured for young learners",
        details: [
          { title: "Age Group", value: "6–8 years / Children in Class 1 or Class 2" },
          { title: "Duration", value: "Class 1 – 16 months | Class 2 – 12 months" },
          { title: "Sessions", value: "1 session per week" },
          { title: "Session Time", value: "2 hours and 15 minutes" },
          { title: "Batch Size", value: "10 to 15 students" },
          { title: "Next Step", value: "Prepared to enter the Foundation level" },
        ],
      },

      foundation: {
        tab: "Foundation Module",
        badge: "Foundation Module",
        title: "A stronger foundation for class 3 and above",
        description:
          "Designed for children in Class 3 and above, the Foundation Module develops life skills, number visualization, and strong Abacus based calculation abilities.",
        stats: [
          { label: "Age Group", value: "8–12 years" },
          { label: "Class Level", value: "Classes 3 to 7" },
          { label: "Levels", value: "Foundation – 4" },
          { label: "Session", value: "1 per week" },
        ],
        overviewTitle: "Foundation Module Outcomes",
        overviewText:
          "This level builds the base for advanced mental arithmetic while improving focus and academic readiness.",
        points: [
          "Developing life skills",
          "Visualization of numbers in the form of beads",
          "Abacus learning to do addition, subtraction, multiplication, and division",
          "Increased focus and accuracy",
        ],
        overviewImage: module1,
        brain: false,
        detailsTitle: "Course Details",
        details: [
          { title: "Age Group", value: "8–12 / Classes 3 to 7" },
          { title: "Number of Levels", value: "11 total levels" },
          { title: "Level Split", value: "Foundation – 4 | Advanced – 4 | Grand Master – 3" },
          { title: "Duration of One Level", value: "13–16 weeks" },
          { title: "Sessions Per Week", value: "1" },
          { title: "Session Duration", value: "2 hours and 15 minutes" },
          { title: "Maximum Students", value: "15 per class" },
        ],
      },

      advanced: {
        tab: "Advanced Module",
        badge: "Advanced Module",
        title: "Sharper computation and faster academic performance",
        description:
          "The Advanced Module develops higher level arithmetic speed, visualization, and accuracy to help children solve complex sums with confidence.",
        stats: [
          { label: "Age Group", value: "8–12 years" },
          { label: "Class Level", value: "Classes 3 to 7" },
          { label: "Levels", value: "Advanced – 4" },
          { label: "Session", value: "1 per week" },
        ],
        overviewTitle: "Advanced Module Outcomes",
        overviewText:
          "This stage focuses on stronger computation, bigger numbers, and quicker problem solving ability.",
        points: [
          "Improved computation skills",
          "Ability to do 3 & 4 digit addition and subtraction",
          "Visualization of multiplication tables up to 999 and 9999",
          "Ability to solve linear and decimal equations quickly",
          "Faster arithmetic calculations for better academic performance",
          "Speed and accuracy",
        ],
        overviewImage: module2,
        brain: false,
        detailsTitle: "Course Details",
        details: [
          { title: "Age Group", value: "8–12 / Classes 3 to 7" },
          { title: "Number of Levels", value: "11 total levels" },
          { title: "Level Split", value: "Foundation – 4 | Advanced – 4 | Grand Master – 3" },
          { title: "Duration of One Level", value: "13–16 weeks" },
          { title: "Sessions Per Week", value: "1" },
          { title: "Session Duration", value: "2 hours and 15 minutes" },
          { title: "Maximum Students", value: "15 per class" },
        ],
      },

      grandmaster: {
        tab: "Grand Master Module",
        badge: "Grand Master Module",
        title: "Advanced mastery for academics and competitive excellence",
        description:
          "The Grand Master Module helps students apply SIP Abacus methodology to advanced school mathematics, competitive exams, and higher level calculations.",
        stats: [
          { label: "Age Group", value: "8–12 years" },
          { label: "Class Level", value: "Classes 3 to 7" },
          { label: "Levels", value: "Grand Master – 3" },
          { label: "Session", value: "1 per week" },
        ],
        overviewTitle: "Grand Master Module Outcomes",
        overviewText:
          "This stage prepares students for complex arithmetic, stronger academic application, and competitive confidence.",
        points: [
          "Application of SIP Abacus methodology in regular school academics, competitive exams, and higher studies",
          "Competitive edge over peers in exams such as Math Olympiads and IITs",
          "Expertise in complex calculations",
          "Orientation to cube roots and square roots",
          "Ability to solve complex linear equations",
          "Expertise in solving decimal multiplication and division sums",
        ],
        overviewImage: module3,
        brain: false,
        detailsTitle: "Course Details",
        details: [
          { title: "Age Group", value: "8–12 / Classes 3 to 7" },
          { title: "Number of Levels", value: "11 total levels" },
          { title: "Level Split", value: "Foundation – 4 | Advanced – 4 | Grand Master – 3" },
          { title: "Duration of One Level", value: "13–16 weeks" },
          { title: "Sessions Per Week", value: "1" },
          { title: "Session Duration", value: "2 hours and 15 minutes" },
          { title: "Maximum Students", value: "15 per class" },
        ],
      },
    }),
    []
  );

  const currentModule = moduleData[activeModule];

  useEffect(() => {
    const items = document.querySelectorAll(
      ".reveal-up, .reveal-left, .reveal-right, .reveal-scale"
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.2 }
    );

    items.forEach((item) => observer.observe(item));

    const brainSection = document.querySelector(".brain-section");
    const brainObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBrainActive(true);
        }
      },
      { threshold: 0.35 }
    );

    if (brainSection) brainObserver.observe(brainSection);

    return () => {
      observer.disconnect();
      brainObserver.disconnect();
    };
  }, [activeModule]);

  const handleModuleChange = (key) => {
    setActiveModule(key);
    setBrainActive(false);

    setTimeout(() => {
      if (tabsRef.current) {
        const navbar = document.querySelector(".navbar");
        const navbarHeight = navbar ? navbar.offsetHeight : 100;
        const extraGap = 20;

        const tabsTop =
          tabsRef.current.getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({
          top: tabsTop - navbarHeight - extraGap,
          behavior: "smooth",
        });
      }
    }, 80);
  };

  return (
    <>
    <SEO
      title="Abacus Courses & Programs | SIP Abacus Malkajgiri Hyderabad"
      description="Explore SIP Abacus Malkajgiri courses – Junior, Foundation, Advanced & Grand Master modules for children aged 6-12. Structured abacus training to improve mental math, concentration, speed & accuracy. Enroll for demo class today!"
      keywords="abacus courses Malkajgiri, SIP Abacus course details, abacus junior module, abacus foundation course, abacus advanced module, grand master abacus course, mental arithmetic program for kids, abacus training modules, abacus classes for 6-12 years, abacus syllabus Hyderabad, best abacus course for children, abacus batch details, abacus fee structure Malkajgiri, brain development course for kids, math skill improvement course, abacus levels explained, abacus classes near Secunderabad"
      canonicalPath="/courses"
    />
    <section className="courses-page">
      <div className="container">
        <div className="module-tabs-wrap reveal-up" ref={tabsRef}>
          <div className="module-tabs">
            {Object.keys(moduleData).map((key) => (
              <button
                key={key}
                className={`module-tab ${activeModule === key ? "active" : ""}`}
                onClick={() => handleModuleChange(key)}
              >
                {moduleData[key].tab}
              </button>
            ))}
          </div>
        </div>

        <div ref={contentRef}>
          <div className="courses-hero reveal-up">
            <span className="hero-badge">{currentModule.badge}</span>
            <h1>{currentModule.title}</h1>
            <p>{currentModule.description}</p>

            <div className="hero-info-strip">
              {currentModule.stats.map((item, index) => (
                <div className="hero-info-card" key={index}>
                  <h4>{item.label}</h4>
                  <p>{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {activeModule === "junior" && (
            <>
              <div className="journey-section">
                <div className="section-heading reveal-up">
                  <span className="section-tag">Learning Journey</span>
                  <h2>{currentModule.journey.title}</h2>
                  <p>{currentModule.journey.text}</p>
                </div>

                <div className="journey-grid">
                  <div className="journey-card reveal-left">
                    <div className="journey-icon">😟</div>
                    <h3>Before Joining</h3>
                    <p>{currentModule.journey.before}</p>
                  </div>

                  <div className="journey-card highlight reveal-up">
                    <div className="journey-icon">🎯</div>
                    <h3>During Learning</h3>
                    <ul>
                      {currentModule.journey.learning.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="journey-card reveal-right">
                    <div className="journey-icon">🚀</div>
                    <h3>After Completion</h3>
                    <p>{currentModule.journey.after}</p>
                  </div>
                </div>
              </div>

              <div className="brain-section">
                <div className="section-heading reveal-up">
                  <span className="section-tag">Brain Activation</span>
                  <h2>Training both sides of the brain together</h2>
                  <p>
                    SIP Abacus Malkajgiri does more than teach arithmetic. It helps activate logical,
                    visual, creative, and concentration based abilities together through
                    repeated practice and mental visualization.
                  </p>
                </div>

                <div className={`brain-layout ${brainActive ? "active" : ""}`}>
                  <div className="brain-side left reveal-left">
                    <h3>Left Brain Activation</h3>
                    <p className="brain-side-subtitle">Logic · Numbers · Accuracy</p>

                    <div className={`brain-point ${brainActive ? "active" : ""}`}>
                      <span>01</span>
                      <div>
                        <h4>Basic understanding of Abacus</h4>
                        <p>
                          Children begin to understand number structure in a simple,
                          visual way.
                        </p>
                      </div>
                    </div>

                    <div className={`brain-point ${brainActive ? "active" : ""}`}>
                      <span>02</span>
                      <div>
                        <h4>Calculation accuracy</h4>
                        <p>
                          Regular practice improves correctness and confidence in
                          solving sums.
                        </p>
                      </div>
                    </div>

                    <div className={`brain-point ${brainActive ? "active" : ""}`}>
                      <span>03</span>
                      <div>
                        <h4>Tables from 2 to 9</h4>
                        <p>
                          Children strengthen number memory and recall with structured
                          repetition.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="brain-visual reveal-scale">
                    <div className="brain-glow brain-glow-1"></div>
                    <div className="brain-glow brain-glow-2"></div>

                    <div className={`brain-image-wrap ${brainActive ? "active" : ""}`}>
                      <img
                        src={brainImg}
                        alt="Brain activation"
                        className="brain-image"
                      />
                    </div>

                    <div className="brain-label left-label">Logic</div>
                    <div className="brain-label right-label">Creativity</div>
                  </div>

                  <div className="brain-side right reveal-right">
                    <h3>Right Brain Activation</h3>
                    <p className="brain-side-subtitle">Memory · Focus · Imagination</p>

                    <div className={`brain-point ${brainActive ? "active" : ""}`}>
                      <span>01</span>
                      <div>
                        <h4>Improved attention span</h4>
                        <p>
                          Children learn to stay focused longer during structured
                          activities.
                        </p>
                      </div>
                    </div>

                    <div className={`brain-point ${brainActive ? "active" : ""}`}>
                      <span>02</span>
                      <div>
                        <h4>Better brain coordination</h4>
                        <p>
                          Learning supports coordination between left and right brain
                          functions.
                        </p>
                      </div>
                    </div>

                    <div className={`brain-point ${brainActive ? "active" : ""}`}>
                      <span>03</span>
                      <div>
                        <h4>Motor skill development</h4>
                        <p>
                          Finger movement and visual focus help improve fine motor
                          control.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeModule !== "junior" && (
            <div className="module-overview-section">
              <div className="section-heading reveal-up">
                <span className="section-tag">Module Overview</span>
                <h2>{currentModule.overviewTitle}</h2>
                <p>{currentModule.overviewText}</p>
              </div>

              <div className="module-overview-card reveal-up">
                <div className="module-overview-left">
                  <span className="module-overview-mini-badge">{currentModule.badge}</span>
                  <h3>{currentModule.overviewTitle}</h3>

                  <ul className="module-points-list">
                    {currentModule.points.map((item, index) => (
                      <li key={index}>
                        <span className="module-bullet">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="module-overview-right">
                  <img
                    src={currentModule.overviewImage}
                    alt={currentModule.overviewTitle}
                    className="module-overview-image"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="details-section">
            <div className="section-heading reveal-up">
              <span className="section-tag">Course Details</span>
              <h2>{currentModule.detailsTitle}</h2>
            </div>

            <div className="details-grid">
              {currentModule.details.map((item, index) => (
                <div className="detail-box reveal-up" key={index}>
                  <h4>{item.title}</h4>
                  <p>{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

export default Courses;